import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X, Maximize2, Volume2, VolumeX, SkipBack, SkipForward, Repeat1 } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import AudioReactiveBars from './AudioReactiveBars';

const FloatingPlayer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    togglePlayPause,
    seek,
    setVolume,
    closePlayer,
    queue,
    hasNext,
    hasPrevious,
    playNext,
    playPrevious,
    isRepeatOne,
    toggleRepeatOne,
  } = useAudioPlayer();
  const inPlaylist = queue.length > 1;

  // Hide floating player if on the player page for current track, on the
  // standalone embed page (iframe-friendly, no app chrome), or if no track
  // loaded.
  const shouldShow =
    currentTrack &&
    !location.pathname.startsWith(`/player/${currentTrack.id}`) &&
    !location.pathname.startsWith('/embed/');

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleExpandClick = () => {
    if (currentTrack) {
      navigate(`/player/${currentTrack.id}`);
    }
  };

  const toggleMute = () => {
    setVolume(volume > 0 ? 0 : 1);
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[90] pointer-events-none"
        >
          <div className="container mx-auto px-4 pb-4 max-w-4xl pointer-events-auto">
            <div
              className="relative overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-md"
              style={{
                backgroundColor: 'rgba(10, 10, 10, 0.85)',
                borderColor: 'var(--border-color)',
              }}
            >
              {/* Reactive spectrum easter egg — a real FFT of the live audio,
                  kept ALMOST invisible (opacity ~0.08) so it reads as a faint
                  shimmer most people never consciously see, and a "wait, is that
                  reacting to the music?!" moment for the one who leans in.
                  Decorative + reduced-motion aware (renders nothing then). */}
              <AudioReactiveBars
                className="absolute inset-x-0 bottom-0 h-full w-full opacity-[0.08] pointer-events-none z-0"
              />

              {/* Progress Bar */}
              <div className="relative z-10 px-4 pt-3 pb-2" style={{ borderRadius: '16px 16px 0 0' }}>
                <div
                  className="h-1.5 bg-neutral-800 rounded-full overflow-hidden cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percentage = x / rect.width;
                    seek(percentage * duration);
                  }}
                >
                  <div
                    className="h-full bg-gradient-to-r from-soundwave-500 to-neon-500 transition-all duration-100"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Player Content */}
              <div className="relative z-10 px-4 pb-4 pt-2">
                <div className="flex items-center gap-4">
                  {/* Previous — only with an active playlist */}
                  {inPlaylist && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={playPrevious}
                      disabled={!hasPrevious}
                      aria-label="Previous track"
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <SkipBack className="w-5 h-5" fill="currentColor" />
                    </motion.button>
                  )}

                  {/* Play/Pause Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={togglePlayPause}
                    className="w-12 h-12 flex-shrink-0 rounded-xl bg-gradient-to-r from-soundwave-500 to-neon-500 hover:from-soundwave-600 hover:to-neon-600 flex items-center justify-center shadow-lg transition-all"
                    disabled={currentTrack?.processingStatus !== 'completed'}
                  >
                    {isPlaying ? (
                      <Pause className="w-5 h-5 text-white" fill="white" />
                    ) : (
                      <Play className="w-5 h-5 text-white" fill="white" />
                    )}
                  </motion.button>

                  {/* Next — only with an active playlist */}
                  {inPlaylist && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={playNext}
                      disabled={!hasNext}
                      aria-label="Next track"
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                    >
                      <SkipForward className="w-5 h-5" fill="currentColor" />
                    </motion.button>
                  )}

                  {/* Repeat-one — slot is always rendered so toggling it on
                      doesn't shift the row (no layout shift when the state
                      flips). Active state tints the icon soundwave; inactive
                      keeps it in the same muted white as the skip controls so
                      it reads as part of the transport cluster. */}
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleRepeatOne}
                    aria-label={isRepeatOne ? 'Turn off repeat' : 'Repeat current song'}
                    aria-pressed={isRepeatOne}
                    title={isRepeatOne ? 'Repeat: on' : 'Repeat current song'}
                    className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                      isRepeatOne
                        ? 'text-soundwave-400 hover:text-soundwave-300 bg-soundwave-500/15'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Repeat1 className="w-5 h-5" />
                  </motion.button>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold truncate text-base" style={{ color: 'var(--text-primary)' }}>
                      {currentTrack?.title || currentTrack?.filename || 'Unknown Track'}
                    </h4>
                    <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      <span>{formatTime(currentTime)}</span>
                      <span>/</span>
                      <span>{formatTime(duration)}</span>
                      {currentTrack?.processingStatus === 'pending' && (
                        <span className="text-yellow-500">• Processing...</span>
                      )}
                    </div>
                  </div>

                  {/* Desktop Controls */}
                  <div className="hidden md:flex items-center gap-3">
                    {/* Volume Control */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleMute}
                        className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {volume > 0 ? (
                          <Volume2 className="w-5 h-5" />
                        ) : (
                          <VolumeX className="w-5 h-5" />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => setVolume(parseFloat(e.target.value))}
                        className="w-20 h-1 bg-neutral-700 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #ff4800 0%, #06b6d4 ${volume * 100}%, #404040 ${volume * 100}%, #404040 100%)`,
                        }}
                      />
                    </div>

                    {/* Expand Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleExpandClick}
                      className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      title="Expand to full player"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </motion.button>

                    {/* Close Button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={closePlayer}
                      className="p-2 rounded-lg hover:bg-red-500/10 hover:text-red-500 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      title="Close player"
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>

                  {/* Mobile Controls (Simplified) */}
                  <div className="flex md:hidden items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleExpandClick}
                      className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <Maximize2 className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={closePlayer}
                      className="p-2 rounded-lg hover:bg-red-500/10 hover:text-red-500 transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingPlayer;
